<template>
    <section class="time-section" aria-label="Choose an available time">
        <div v-if="loading" class="time-state" aria-live="polite">
            Loading available times...
        </div>
        <div v-else-if="error" class="time-state error" role="alert">
            {{ error }}
        </div>
        <div v-else-if="closed" class="time-state">
            <strong>Closed on this date.</strong>
            <button type="button" class="secondary-action" @click="$emit('choose-date')">
                Choose another date
            </button>
        </div>
        <div v-else-if="!hasAvailableSlots" class="time-state" aria-live="polite">
            <strong>No available times for your current preferences on this date.</strong>
            <span v-if="canUseAny && fallbackServiceName" class="fallback-detail">
                Try Any Specialist for {{ fallbackServiceName }} to see more times.
            </span>
            <div class="state-actions">
                <button type="button" class="secondary-action" @click="$emit('choose-date')">
                    Choose another date
                </button>
                <button v-if="canUseAny" type="button" class="primary-action" @click="$emit('use-any')">
                    Use Any Specialist
                </button>
            </div>
        </div>
        <div v-else class="time-groups">
            <section v-for="group in timeGroups" :key="group.value" class="time-group">
                <h2>{{ group.label }}</h2>
                <div class="time-grid">
                    <button
                        v-for="slot in group.slots"
                        :key="slot.start_time"
                        type="button"
                        class="time-item"
                        :class="{
                            active: Number(selectedSlotStart) === Number(slot.start_time),
                            disabled: !slot.available
                        }"
                        :aria-label="slot.time_text"
                        :disabled="!slot.available"
                        @click="$emit('select-time', slot)"
                    >
                        {{ compactTime(slot.time_text) }}
                    </button>
                </div>
            </section>
        </div>
    </section>
</template>

<script>
const PERIODS = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'evening', label: 'Evening' }
]

export default {
    name: 'BookingTimeSection',
    props: {
        slots: {
            type: Array,
            default: () => []
        },
        selectedSlotStart: {
            type: [Number, String],
            default: null
        },
        loading: Boolean,
        error: {
            type: String,
            default: ''
        },
        closed: Boolean,
        hasAvailableSlots: Boolean,
        canUseAny: Boolean,
        fallbackServiceName: {
            type: String,
            default: ''
        }
    },
    computed: {
        /** 按上午、下午和晚上组织完整时间网格。 */
        timeGroups() {
            return PERIODS.map(period => ({
                ...period,
                slots: this.slots.filter(slot => slot.period === period.value)
            })).filter(period => period.slots.length > 0)
        }
    },
    methods: {
        /** 分组标题已表达时段，因此按钮中移除重复的 AM/PM。 */
        compactTime(value) {
            return String(value || '').replace(/\s*(AM|PM)$/i, '')
        }
    }
}
</script>

<style lang="scss" scoped>
.time-section {
    padding: .5rem 1rem 1.25rem;
}

.time-groups {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.time-group h2 {
    margin: 0 0 .625rem;
    color: #626262;
    font-size: .8125rem;
    font-weight: 600;
}

.time-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(4.75rem, 1fr));
    gap: .5rem;
}

.time-item {
    min-height: 2.5rem;
    padding: .4rem .15rem;
    border: 1px solid #1e3c3e;
    border-radius: .25rem;
    color: #1e3c3e;
    background: #fff;
    font-family: inherit;
    font-size: .75rem;

    &.active {
        color: #fff;
        border-color: #1e3c3e;
        background: #1e3c3e;
        font-weight: 600;
    }

    &.disabled {
        color: #9ca3af;
        border-color: #e5e7eb;
        background: #e5e7eb;
        opacity: 1;
    }

    &:focus-visible {
        outline: 2px solid #71ce00;
        outline-offset: 2px;
    }
}

.time-state {
    display: flex;
    min-height: 7.5rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: .75rem;
    padding: 1.25rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: .5rem;
    color: #666;
    background: #f8f9f9;
    font-size: .8125rem;
    text-align: center;

    strong {
        color: #333;
        font-size: .875rem;
    }

    &.error {
        color: #c2413b;
        border-color: #f2d0cd;
        background: #fff7f6;
    }
}

.fallback-detail {
    color: #777;
}

.state-actions {
    display: flex;
    justify-content: center;
    gap: .5rem;
}

.secondary-action,
.primary-action {
    min-height: 2.5rem;
    padding: .5rem .75rem;
    border-radius: .375rem;
    font-family: inherit;
    font-size: .75rem;
}

.secondary-action {
    border: 1px solid #1e3c3e;
    color: #1e3c3e;
    background: #fff;
}

.primary-action {
    border: 1px solid #1e3c3e;
    color: #fff;
    background: #1e3c3e;
}
</style>
